import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getToken, getRefreshToken, setToken, clearToken } from './auth';
import type { AuthTokens } from '@pms/shared';

// ─── Axios instance ───────────────────────────────────────────────────────────

const _apiBase = import.meta.env.VITE_API_URL;
const _baseURL = _apiBase ? `${_apiBase}/v1` : '/v1';

export const api = axios.create({
  baseURL: _baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15_000,
});

// ─── Request interceptor — attach Bearer token ────────────────────────────────

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ─── Response interceptor — handle 401 with token refresh ────────────────────

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (reason: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Pending-password fence backstop: the server 403s a must-change account on
    // every route except set-password. If any call hits it, route to the
    // set-password screen (the route guards normally catch this, but an
    // in-flight request mid-session must not silently fail).
    const code = (error.response?.data as { code?: string } | undefined)?.code;
    if (
      error.response?.status === 403 &&
      code === 'PASSWORD_CHANGE_REQUIRED' &&
      window.location.pathname !== '/set-password'
    ) {
      window.location.href = '/set-password';
      return Promise.reject(error);
    }

    // Only attempt token refresh on 401 Unauthorized — not on network errors, 502, 503, etc.
    const status = error.response?.status;
    if (status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // No response at all (network error / server down) — don't logout
    if (!error.response) {
      return Promise.reject(error);
    }

    // Don't try to refresh for auth endpoints (login returns 401 on bad credentials)
    if (
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/register')
    ) {
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearToken();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue requests that come in while refresh is happening
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
          }
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearToken();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post<AuthTokens>(`${_baseURL}/auth/refresh`, {
        refreshToken,
      });

      setToken(data);
      processQueue(null, data.accessToken);

      if (originalRequest.headers) {
        originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`;
      }
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearToken();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
